import React from "react";

type CutFrameSide = "both" | "top-left" | "bottom-right";

interface CutFrameProps {
    /** Thickness of the frame bars in pixels */
    thickness?: number;
    /** Color of the frame bars */
    color?: string;
    /** Which sides to show: "both" (default), "top-left" (top + left lines), "bottom-right" (bottom + right lines) */
    side?: CutFrameSide;
    /** Content inside the frame */
    children: React.ReactNode;
    /** Additional className on the outer container */
    className?: string;
}

/**
 * @version 1.0.0
 * @published 2026-04-08
 * @history
 *   1.0.0 — Initial publish. Decorative frame with cut corners (The Cut brand device).
 */
export function CutFrame({
    thickness = 4,
    color = "rgb(var(--color-line-base))",
    side = "both",
    children,
    className
}: CutFrameProps): React.ReactElement {
    const t = `${thickness}px`;
    const showTop = side === "both" || side === "top-left";
    const showRight = side === "both" || side === "bottom-right";
    const showBottom = side === "both" || side === "bottom-right";
    const showLeft = side === "both" || side === "top-left";

    return (
        <div className={`relative ${className ?? ""}`}>
            {/* Top: skips left corner */}
            {showTop && (
                <div
                    className="absolute top-0 right-0"
                    style={{ left: t, height: t, backgroundColor: color }}
                />
            )}
            {/* Right: skips bottom corner */}
            {showRight && (
                <div
                    className="absolute top-0 right-0"
                    style={{ bottom: t, width: t, backgroundColor: color }}
                />
            )}
            {/* Bottom: skips right corner */}
            {showBottom && (
                <div
                    className="absolute bottom-0 left-0"
                    style={{ right: t, height: t, backgroundColor: color }}
                />
            )}
            {/* Left: skips top corner */}
            {showLeft && (
                <div
                    className="absolute left-0 bottom-0"
                    style={{ top: t, width: t, backgroundColor: color }}
                />
            )}

            {children}
        </div>
    );
}
